import { useState, useEffect, useRef } from 'react';
import { useNotification } from '../contexts/NotificationContext';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';
import {
  Package,
  Truck,
  RefreshCw,
  Download,
  Check,
  X,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ExternalLink,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  MapPin,
} from 'lucide-react';
import { shipmentApi, userApi, type Shipment } from '../lib/api';
import { EmptyState } from '../components/EmptyState';

const PER_PAGE_OPTIONS = [10, 20, 50, 100, 250, 500];

const statusConfig: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  CREATED: { label: 'Utworzona', color: 'text-accent-info bg-info/20', icon: Clock },
  CONFIRMED: { label: 'Potwierdzona', color: 'text-purple-400 bg-purple-500/20', icon: Check },
  DISPATCHED: { label: 'Nadana', color: 'text-indigo-400 bg-indigo-500/20', icon: Truck },
  IN_TRANSIT: { label: 'W transporcie', color: 'text-cyan-400 bg-cyan-500/20', icon: Truck },
  OUT_FOR_DELIVERY: { label: 'W doręczeniu', color: 'text-warning bg-warning/20', icon: MapPin },
  DELIVERED: { label: 'Dostarczona', color: 'text-success bg-success/20', icon: CheckCircle },
  RETURNED: { label: 'Zwrócona', color: 'text-orange-400 bg-orange-500/20', icon: AlertCircle },
  FAILED: { label: 'Nieudana', color: 'text-danger bg-danger/20', icon: XCircle },
};

export function ShipmentsPage() {
  const { showError } = useNotification();
  const confirmDialog = useConfirmDialog();
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(() => {
    const stored = localStorage.getItem('ecomflow_shipments_perPage');
    return stored ? parseInt(stored, 10) : 20;
  });
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [perPageDropdownOpen, setPerPageDropdownOpen] = useState(false);
  const perPageDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (perPageDropdownRef.current && !perPageDropdownRef.current.contains(e.target as Node)) {
        setPerPageDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Load server-side preferences on mount
  useEffect(() => {
    userApi.getPreferences().then(({ data }) => {
      const serverPerPage = (data as Record<string, unknown>)?.shipmentsPerPage;
      if (typeof serverPerPage === 'number' && serverPerPage > 0) {
        const localPerPage = localStorage.getItem('ecomflow_shipments_perPage');
        if (!localPerPage) {
          setPerPage(serverPerPage);
          setPage(1);
        }
      }
    }).catch(() => {});
  }, []);

  // Persist perPage
  useEffect(() => {
    localStorage.setItem('ecomflow_shipments_perPage', perPage.toString());
    userApi.updatePreferences({ shipmentsPerPage: perPage }).catch(() => {});
  }, [perPage]);

  useEffect(() => {
    loadShipments();
  }, [page, perPage, statusFilter]);

  const loadShipments = async () => {
    try {
      setLoading(true);
      const { data } = await shipmentApi.getAll({
        page,
        limit: perPage,
        status: statusFilter || undefined,
      });
      setShipments(data.shipments);
      setTotalPages(data.pagination.pages);
      setTotal(data.pagination.total);
    } catch (error) {
      console.error('Failed to load shipments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncStatus = async (shipmentId: string) => {
    setSyncing(shipmentId);
    try {
      await shipmentApi.syncStatus(shipmentId);
      await loadShipments();
    } catch (error) {
      console.error('Failed to sync status:', error);
    } finally {
      setSyncing(null);
    }
  };

  const handleGetLabel = async (shipmentId: string) => {
    try {
      const { data } = await shipmentApi.getLabel(shipmentId);

      // Convert base64 to blob and download
      const byteCharacters = atob(data.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: data.contentType });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = data.filename || 'etykieta.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to get label:', error);
      showError(error, 'label');
    }
  };

  const handleBuy = async (shipmentId: string) => {
    if (!(await confirmDialog({
      title: 'Opłać przesyłkę',
      message: 'Czy na pewno chcesz opłacić tę przesyłkę?',
      subtitle: 'Opłata pobierana jest z konta InPost.',
      confirmLabel: 'Opłać',
      danger: false,
    }))) return;
    try {
      await shipmentApi.buy(shipmentId);
      await loadShipments();
    } catch (error) {
      console.error('Failed to buy shipment:', error);
    }
  };

  const handleCancel = async (shipmentId: string) => {
    if (!(await confirmDialog({
      title: 'Anuluj przesyłkę',
      message: 'Czy na pewno chcesz anulować tę przesyłkę?',
      subtitle: 'Anulowanie w InPost — operacja nieodwracalna.',
      confirmLabel: 'Anuluj przesyłkę',
    }))) return;
    try {
      await shipmentApi.cancel(shipmentId);
      await loadShipments();
    } catch (error) {
      console.error('Failed to cancel shipment:', error);
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getServiceLabel = (shipmentData: Record<string, unknown>) => {
    const service = shipmentData.service as string;
    switch (service) {
      case 'inpost_locker_standard':
        return 'Paczkomat';
      case 'inpost_courier_standard':
        return 'Kurier';
      case 'inpost_courier_express':
        return 'Kurier Express';
      default:
        return service || '-';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">Wysyłki</h1>
          <p className="text-text-secondary mt-1">
            Zarządzaj przesyłkami kurierskimi
          </p>
        </div>
        <button
          onClick={loadShipments}
          disabled={loading}
          className="btn-secondary flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Odśwież
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex items-center gap-4 flex-wrap">
          <div>
            <label className="label">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="input min-w-[180px]"
            >
              <option value="">Wszystkie</option>
              {Object.entries(statusConfig).map(([value, config]) => (
                <option key={value} value={value}>
                  {config.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1" />
          <div className="text-sm text-text-secondary">
            Znaleziono: <span className="text-text-primary font-medium">{total}</span> przesyłek
          </div>
        </div>
      </div>

      {/* Shipments Table */}
      <div className="card overflow-hidden p-0">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-accent-primary" />
          </div>
        ) : shipments.length === 0 ? (
          <EmptyState
            icon={Package}
            title="Brak przesyłek"
            description="Utwórz przesyłkę z poziomu zamówienia"
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border-subtle">
                  <th className="text-left text-sm font-medium text-text-secondary px-6 py-4">Zamówienie</th>
                  <th className="text-left text-sm font-medium text-text-secondary px-6 py-4">Nr przesyłki</th>
                  <th className="text-left text-sm font-medium text-text-secondary px-6 py-4">Typ</th>
                  <th className="text-left text-sm font-medium text-text-secondary px-6 py-4">Status</th>
                  <th className="text-left text-sm font-medium text-text-secondary px-6 py-4">Data</th>
                  <th className="text-right text-sm font-medium text-text-secondary px-6 py-4">Akcje</th>
                </tr>
              </thead>
              <tbody>
                {shipments.map((shipment) => {
                  const status = statusConfig[shipment.status] || statusConfig.CREATED;
                  const StatusIcon = status.icon;
                  const shipmentData = shipment.shipmentData as Record<string, unknown>;

                  return (
                    <tr
                      key={shipment.id}
                      className="border-b border-border-subtle hover:bg-surface-hover/50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-text-primary font-medium">
                            #{shipment.order?.orderNumber}
                          </p>
                          <p className="text-sm text-text-secondary">
                            {shipment.order?.customerName}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className="text-text-primary font-mono">
                            {shipment.trackingNumber || '-'}
                          </span>
                          {shipment.trackingNumber && (
                            <a
                              href={`https://inpost.pl/sledzenie-przesylek?number=${shipment.trackingNumber}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-text-secondary hover:text-accent-primary"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Truck className="w-4 h-4 text-text-secondary" />
                          <span className="text-text-secondary">{getServiceLabel(shipmentData)}</span>
                        </div>
                        {typeof shipmentData.targetPoint === 'string' && shipmentData.targetPoint && (
                          <p className="text-xs text-text-muted mt-1">
                            <MapPin className="w-3 h-3 inline" /> {shipmentData.targetPoint}
                          </p>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${status.color}`}>
                          <StatusIcon className="w-3.5 h-3.5" />
                          {status.label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-text-secondary text-sm">{formatDate(shipment.createdAt)}</p>
                        {shipment.deliveredAt && (
                          <p className="text-xs text-success">
                            Dostarczono: {formatDate(shipment.deliveredAt)}
                          </p>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          {/* Sync status */}
                          <button
                            onClick={() => handleSyncStatus(shipment.id)}
                            disabled={syncing === shipment.id}
                            className="p-2 text-text-secondary hover:text-text-primary hover:bg-surface-hover/60 rounded-lg transition-colors"
                            title="Synchronizuj status"
                          >
                            {syncing === shipment.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <RefreshCw className="w-4 h-4" />
                            )}
                          </button>

                          {/* Get label */}
                          {shipment.status !== 'FAILED' && (
                            <button
                              onClick={() => handleGetLabel(shipment.id)}
                              className="p-2 text-text-secondary hover:text-accent-primary hover:bg-accent-primary/10 rounded-lg transition-colors"
                              title="Pobierz etykietę"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                          )}

                          {/* Buy shipment */}
                          {shipment.status === 'CREATED' && (
                            <button
                              onClick={() => handleBuy(shipment.id)}
                              className="p-2 text-text-secondary hover:text-success hover:bg-success/10 rounded-lg transition-colors"
                              title="Opłać przesyłkę"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                          )}

                          {/* Cancel shipment */}
                          {['CREATED', 'CONFIRMED'].includes(shipment.status) && (
                            <button
                              onClick={() => handleCancel(shipment.id)}
                              className="p-2 text-text-secondary hover:text-danger hover:bg-danger/10 rounded-lg transition-colors"
                              title="Anuluj przesyłkę"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {total > 0 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-border-subtle">
            <div className="flex items-center gap-4">
              <p className="text-sm text-text-secondary">
                Pokazano {(page - 1) * perPage + 1} - {Math.min(page * perPage, total)} z {total}
              </p>
              {/* Per-page selector */}
              <div className="relative" ref={perPageDropdownRef}>
                <button
                  onClick={() => setPerPageDropdownOpen(!perPageDropdownOpen)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-surface-hover hover:bg-surface-muted border border-border rounded-lg text-sm text-text-secondary transition-all"
                >
                  <span>{perPage} / str.</span>
                  <ChevronDown className={`w-4 h-4 text-text-muted transition-transform ${perPageDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {perPageDropdownOpen && (
                  <div className="absolute bottom-full left-0 mb-2 w-28 bg-surface-hover border border-border rounded-xl shadow-xl z-20 py-2">
                    {PER_PAGE_OPTIONS.map(n => (
                      <button
                        key={n}
                        onClick={() => {
                          setPerPage(n);
                          setPage(1);
                          setPerPageDropdownOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm transition-all ${
                          perPage === n
                            ? 'bg-accent-primary/20 text-accent-primary'
                            : 'text-text-secondary hover:bg-surface-muted'
                        }`}
                      >
                        {n} / str.
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="p-2 text-text-secondary hover:text-text-primary hover:bg-surface-hover/60 rounded-lg transition-colors disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-text-secondary px-3">
                  {page} / {totalPages}
                </span>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  className="p-2 text-text-secondary hover:text-text-primary hover:bg-surface-hover/60 rounded-lg transition-colors disabled:opacity-50"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
